import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { CheckCircle2, Package, ShoppingCart, Tag } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReceiptCarts } from '../../hooks/useReceiptCarts';

export interface LocationOrdersModalRef {
  open: (entryId: number, receiptId: number) => void;
  close: () => void;
}

export const LocationOrdersModal = forwardRef<LocationOrdersModalRef>((_, ref) => {
  const { t } = useTranslation('merchandiseV3');
  const modalRef = useRef<ModalCenterRef>(null);
  const [entryId, setEntryId] = useState<number | null>(null);
  const [receiptId, setReceiptId] = useState<number | null>(null);

  // Hook para obtener carritos
  const { data: cartsData, isLoading: isLoadingCarts } = useReceiptCarts(
    entryId || 0,
    receiptId || 0,
    !!(entryId && receiptId),
  );

  useImperativeHandle(ref, () => ({
    open: (entryIdValue: number, receiptIdValue: number) => {
      setEntryId(entryIdValue);
      setReceiptId(receiptIdValue);
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
      setEntryId(null);
      setReceiptId(null);
    },
  }));

  const carts = cartsData?.data || [];

  return (
    <ModalCenter ref={modalRef} title={t('location_orders.modal.title', 'Despachos')} type="info" width="xxl">
      <div className="space-y-4">
        {isLoadingCarts ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : carts.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">
              {t('location_orders.modal.carts.empty', 'No hay carritos utilizados en esta recepción')}
            </p>
          </div>
        ) : (
          <>
            {/* Encabezados */}
            <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-muted/70 rounded-lg text-sm text-foreground">
              <div className="col-span-2">{t('location_orders.modal.carts.code', 'Código')}</div>
              <div className="col-span-2">{t('location_orders.modal.carts.products', 'Productos')}</div>
              <div className="col-span-2">{t('location_orders.modal.carts.quantity', 'Cantidad')}</div>
              <div className="col-span-2 text-center">
                {t('location_orders.modal.carts.dispatch_status', 'Estado Despacho')}
              </div>
              <div className="col-span-2">{t('location_orders.modal.carts.dispatch_type', 'Tipo')}</div>
              <div className="col-span-2 text-center">{t('location_orders.modal.carts.dispatch_id', 'ID Orden')}</div>
            </div>

            {/* Lista de carritos */}
            <ScrollArea className="h-[500px]">
              <div className="space-y-2 pr-4">
                {carts.map((cart) => (
                  <div
                    key={cart.id}
                    className={`grid grid-cols-12 gap-3 px-4 py-4 border rounded-lg transition-colors bg-background ${
                      cart.has_dispatch
                        ? 'border-green-200 dark:border-green-800 hover:border-green-300 dark:hover:border-green-700'
                        : 'border-border hover:border-primary/30'
                    }`}
                  >
                    {/* Código del carrito */}
                    <div className="col-span-2 flex items-center">
                      <div className="flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                        <span className="font-mono font-semibold text-foreground">{cart.code}</span>
                      </div>
                    </div>

                    {/* Total Productos */}
                    <div className="col-span-2 flex items-center">
                      <Package className="w-4 h-4 text-muted-foreground mr-2" />
                      <span className="text-sm text-foreground">
                        {cart.total_products}{' '}
                        {cart.total_products === 1
                          ? t('location_orders.modal.carts.product', 'producto')
                          : t('location_orders.modal.carts.products_plural', 'productos')}
                      </span>
                    </div>

                    {/* Cantidad Total */}
                    <div className="col-span-2 flex items-center">
                      <Tag className="w-4 h-4 text-muted-foreground mr-2" />
                      <span className="text-sm text-foreground">
                        {cart.total_quantity} {t('location_orders.modal.carts.units', 'ud.')}
                      </span>
                    </div>

                    {/* Estado de Despacho */}
                    <div className="col-span-2 flex items-center justify-center">
                      {cart.has_dispatch ? (
                        <Badge variant="default" className="flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          {t('location_orders.modal.carts.dispatched', 'Despachado')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">{t('location_orders.modal.carts.pending', 'Pendiente')}</Badge>
                      )}
                    </div>

                    {/* Tipo de Despacho */}
                    <div className="col-span-2 flex items-center">
                      {cart.dispatch_type ? (
                        <span className="text-sm text-foreground">
                          {cart.dispatch_type === 'LOCATION_ORDER'
                            ? t('location_orders.modal.carts.location_order', 'Ubicación')
                            : t('location_orders.modal.carts.extra_work_order', 'Trabajo Extra')}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </div>

                    {/* ID de la Orden */}
                    <div className="col-span-2 flex items-center justify-center">
                      {cart.dispatch_id ? (
                        <span className="text-sm font-medium text-primary">#{cart.dispatch_id}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        )}
      </div>
    </ModalCenter>
  );
});

LocationOrdersModal.displayName = 'LocationOrdersModal';
